import {CheckCircleOutlined, DeleteOutlined, EditOutlined, PlusOutlined,} from '@ant-design/icons';
import {App, Button, Popconfirm, Tree} from 'antd';
import React, {useEffect, useMemo} from 'react';
import GiroSpin from '../../../components/common/GiroSpin';
import SectionToolbar from '../../../components/common/SectionToolbar';
import {useDeleteGroup, useDeleteVariable, useVariablesGroup} from '../../../hooks/parameters';
import {useTranslation} from '../../../hooks/useTranslation';
import {convertToTreeData, getDisplayText} from './helpers';
import {extractErrorMessage} from '../../../utils/apiError';

interface AssessmentVariableTreeProps {
  selectedKey: string | null;
  onSelect: (item: any, key: string) => void;
  onCreateGroup: () => void;
  onEditGroup: (item: any) => void;
  onCreateVariable: (parent: any) => void;
  onEditVariable: (item: any) => void;
}

const stopPropagation = (e: React.MouseEvent | React.SyntheticEvent) => {
  e.stopPropagation();
};

const AssessmentVariableTree: React.FC<AssessmentVariableTreeProps> = ({
                                                                         selectedKey,
                                                                         onSelect,
                                                                         onCreateGroup,
                                                                         onEditGroup,
                                                                         onCreateVariable,
                                                                         onEditVariable,
                                                                       }) => {
  const {t} = useTranslation();
  const {message: messageApi} = App.useApp();

  // Deletes used to `.catch(console.error)`: the backend answered every failure
  // with HTTP 200, so nothing rejected and the user saw no feedback at all.
  // Now that failures arrive as 4xx/5xx, surface the server's message.
  const reportDeleteError = (error: unknown) =>
    messageApi.error(extractErrorMessage(error, t('messages.deleteError')));
  const {data: variablesGroup, isLoading, error} = useVariablesGroup();
  const deleteGroup = useDeleteGroup();
  const deleteVariable = useDeleteVariable();

  const treeData = useMemo(() => convertToTreeData(variablesGroup || []), [variablesGroup]);

  // Auto-select the first node on initial load.
  useEffect(() => {
    if (treeData.length > 0 && !selectedKey) {
      const first = treeData[0];
      const item = (first as any).data;
      if (item != null && first.key != null) {
        onSelect(item, String(first.key));
      }
    }
  }, [treeData, selectedKey, onSelect]);

  const handleAntdSelect = (selectedKeys: React.Key[], info: any) => {
    if (selectedKeys.length === 0) return;
    onSelect(info.node.data, String(selectedKeys[0]));
  };

  return (
    <>
      <SectionToolbar
        bordered
        title={t('settings.content.impactAssessment.tree.title')}
        extra={
          <Button
            color="default"
            size="medium"
            icon={<PlusOutlined/>}
            onClick={onCreateGroup}
          >
            {t('settings.content.impactAssessment.tree.actions.addGroup')}
          </Button>
        }
      />
      {isLoading && <GiroSpin tip="Loading tree..."/>}
      {error && <div>Error loading tree data</div>}
      {!isLoading && treeData.length === 0 && (
        <div>{t('settings.content.impactAssessment.tree.noVariablesFound')}</div>
      )}
      {treeData.length > 0 && (
        <Tree
          motion={false}
          onSelect={handleAntdSelect}
          treeData={treeData}
          selectedKeys={selectedKey ? [selectedKey] : []}
          titleRender={(node: any) => {
            const item = node.data || {};
            const isGroup = item.idVariableGroup != null && !item.idVariable;
            const isVariable = item.idVariable != null;
            const completed = isGroup && Array.isArray(item.children) && item.children.length > 0;
            return (
              <span className="gt-tree-node">
                <span className="gt-tree-node-label">
                  <span>{getDisplayText(item)}</span>
                  {completed && (
                    <CheckCircleOutlined
                      style={{color: 'var(--ant-color-success, #65c376)'}}
                      title={t('settings.content.impactAssessment.tree.tags.completed')}
                    />
                  )}
                </span>
                <span className="gt-tree-node-actions">
                  {isGroup && (
                    <>
                      <Button
                        type="text"
                        size="small"
                        icon={<PlusOutlined/>}
                        onClick={(e) => {
                          stopPropagation(e);
                          onCreateVariable(item);
                        }}
                        aria-label={t('settings.content.impactAssessment.tree.actions.addVariable')}
                      />
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined/>}
                        onClick={(e) => {
                          stopPropagation(e);
                          onEditGroup(item);
                        }}
                        aria-label={t('settings.content.impactAssessment.tree.actions.editGroup')}
                      />
                      <Popconfirm
                        title={t('settings.content.impactAssessment.tree.actions.deleteGroupConfirm')}
                        okText={t('settings.content.impactAssessment.formulaTable.actions.delete')}
                        cancelText={t('settings.content.impactAssessment.formulaTable.actions.cancel')}
                        okButtonProps={{danger: true, loading: deleteGroup.isPending}}
                        onConfirm={() =>
                          deleteGroup.mutateAsync(item.idVariableGroup).catch(reportDeleteError)
                        }
                      >
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined/>}
                          onClick={stopPropagation}
                          aria-label={t('settings.content.impactAssessment.formulaTable.actions.delete')}
                        />
                      </Popconfirm>
                    </>
                  )}
                  {isVariable && (
                    <>
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined/>}
                        onClick={(e) => {
                          stopPropagation(e);
                          onEditVariable(item);
                        }}
                        aria-label={t('settings.content.impactAssessment.tree.actions.editVariable')}
                      />
                      <Popconfirm
                        title={t('settings.content.impactAssessment.tree.actions.deleteVariableConfirm')}
                        okText={t('settings.content.impactAssessment.formulaTable.actions.delete')}
                        cancelText={t('settings.content.impactAssessment.formulaTable.actions.cancel')}
                        okButtonProps={{danger: true, loading: deleteVariable.isPending}}
                        onConfirm={() =>
                          deleteVariable.mutateAsync(item.idVariable).catch(reportDeleteError)
                        }
                      >
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined/>}
                          onClick={stopPropagation}
                          aria-label={t('settings.content.impactAssessment.formulaTable.actions.delete')}
                        />
                      </Popconfirm>
                    </>
                  )}
                </span>
              </span>
            );
          }}
        />
      )}
    </>
  );
};

export default AssessmentVariableTree;
